import dayjs from "dayjs";
import { getUserDisplayName } from "../../../common/utils/userDisplayName";
import type { TicketRetrieve } from "../../generated-axios-client";
import {
  standbyReasonLabelMap,
  measurementUnitLabelMap,
  priorityLabelMap,
  resolutionLabelMap,
  statusLabelMap,
} from "../ticketConstants";
import {
  formatComponentDetails,
  formatComponentLabel,
} from "../../../common/utils/componentLabel";

export type LogContext = {
  userMap: Record<number, string>;
  materialMap: Record<string, string>;
  componentMap: Record<string, string>;
  serviceMap: Record<string, string>;
  locationMap: Record<string, string>;
  turbineMap: Record<string, string>;
  clientMap: Record<string, string>;
  projectMap: Record<string, string>;
  taskMap: Record<string, string>;
};

export type FieldDiff = {
  label: string;
  before: string;
  after: string;
  // When set, DiffLine renders this as a single free-form line instead of a before/after diff.
  summary?: string;
};

export type WorkLogDiffEntry = {
  old_fields: Record<string, unknown> | null | undefined;
  new_fields: Record<string, unknown> | null | undefined;
  ticket_comment?: string | null;
  ticket_item?: string | null;
  changed_by?: number | null;
};

const FIELD_LABELS: Record<string, string> = {
  status: "Status",
  description: "Description",
  service: "Service",
  priority: "Priority",
  deadline: "Deadline",
  start: "Start Date",
  end: "End Date",
  actual_start: "Actual Start",
  actual_end: "Actual End",
  sent_to_review_at: "Sent to Review",
  standby_from: "Standby Start",
  standby_to: "Standby Until",
  amount: "Amount Used",
  measurement_unit: "Unit",
  batch: "Batch No",
  expiration_date: "Expiration Date",
  coordinator: "Project Coordinator",
  coordinators: "Project Coordinators",
  manager: "Project Manager",
  managers: "Project Managers",
  assignees: "Assignees",
  items: "Tasks",
  materials: "Supplies",
  location: "Site",
  turbine: "Turbine",
  client: "Client",
  client_case_nr: "Case Number",
  summary_text: "Summary",
  delay_reason: "Standby Reason",
  resolution: "Resolution",
  report_done: "Report Sent to Customer",
  rating: "Rating",
  rating_message: "Rating Comment",
  project: "Project",
};

const SKIP_FIELDS = new Set([
  "id",
  "ticket",
  "created_at",
  "updated_at",
  "name",
  "new_files",
  // system-managed / not meaningful user actions
  "total_time",
  "ticket_number",
  // comment fields — handled entirely by buildCommentDiff; skipped here as a safety net
  // in case a comment-linked entry is ever missed by the ticket_comment check
  "content",
  "user",
  "reply_to",
  "ticket_item",
]);

function tokenizeAssignees(val: unknown, ctx: LogContext): string[] {
  if (!Array.isArray(val) || val.length === 0) return [];
  return val.map((a) => {
    const id =
      a !== null && typeof a === "object" ? (a as Record<string, unknown>).assignee : a;
    return ctx.userMap[id as number] ?? `#${id}`;
  });
}

function tokenizeMaterials(val: unknown, ctx: LogContext): string[] {
  if (!Array.isArray(val) || val.length === 0) return [];
  return val.map((m) => {
    const id =
      m !== null && typeof m === "object" ? (m as Record<string, unknown>).material : m;
    return ctx.materialMap[id as string] ?? `#${id}`;
  });
}

// Worklog entries written before the blade → component rename still carry the
// "blade" key, so both spellings resolve to the same component map.
function componentIdOf(item: Record<string, unknown>): string | undefined {
  return (item.component ?? item.blade) as string | undefined;
}

function itemLabel(item: Record<string, unknown>, ctx: LogContext): string {
  const componentId = componentIdOf(item);
  if (componentId && ctx.componentMap[componentId]) return ctx.componentMap[componentId];
  if (item.description) return String(item.description);
  return `#${componentId ?? item.id ?? "?"}`;
}

const TASK_FIELD_LABELS: Record<string, string> = {
  component: "Component",
  blade: "Component",
  service: "Task Service",
  description: "Task Description",
  estimated_time_spent: "Estimated Time",
  actual_time_spent: "Actual Time",
  completed: "Task Completed",
};

function formatTaskFieldValue(field: string, val: unknown, ctx: LogContext): string {
  if (field === "component" || field === "blade")
    return val ? (ctx.componentMap[val as string] ?? `#${val}`) : "";
  if (field === "service") return val ? (ctx.serviceMap[val as string] ?? `#${val}`) : "";
  if (field === "completed") return val ? "Yes" : "No";
  return val === null || val === undefined || val === "" ? "" : String(val);
}

// Tasks (formerly "Blades") are matched by id so that in-place edits (e.g. marking a task
// completed) surface as field-level diffs instead of being lost in a same-membership list diff.
function buildItemDiffs(oldVal: unknown, newVal: unknown, ctx: LogContext): FieldDiff[] {
  const oldItems = (Array.isArray(oldVal) ? oldVal : []) as Record<string, unknown>[];
  const newItems = (Array.isArray(newVal) ? newVal : []) as Record<string, unknown>[];

  const oldById = new Map(oldItems.filter((i) => i.id).map((i) => [i.id as string, i]));
  const newById = new Map(newItems.filter((i) => i.id).map((i) => [i.id as string, i]));

  const removedLabels = oldItems
    .filter((item) => !item.id || !newById.has(item.id as string))
    .map((item) => itemLabel(item, ctx));
  const addedLabels = newItems
    .filter((item) => !item.id || !oldById.has(item.id as string))
    .map((item) => itemLabel(item, ctx));

  const label = FIELD_LABELS.items;
  const diffs: FieldDiff[] = [];
  if (removedLabels.length > 0)
    diffs.push({ label, before: removedLabels.join(", "), after: "" });
  if (addedLabels.length > 0)
    diffs.push({ label, before: "", after: addedLabels.join(", ") });

  for (const [id, oldItem] of oldById) {
    const newItem = newById.get(id);
    if (!newItem) continue;

    const ref = itemLabel(newItem, ctx);
    for (const field of Object.keys(TASK_FIELD_LABELS)) {
      const before = formatTaskFieldValue(field, oldItem[field], ctx);
      const after = formatTaskFieldValue(field, newItem[field], ctx);
      if (before === after) continue;
      diffs.push({
        label: `${label} · ${ref} · ${TASK_FIELD_LABELS[field]}`,
        before,
        after,
      });
    }
  }

  return diffs;
}

const tokenizeUserReferences =
  (userKey: string) =>
  (val: unknown, ctx: LogContext): string[] => {
    if (!Array.isArray(val) || val.length === 0) return [];
    return val.map((entry) => {
      const id =
        entry !== null && typeof entry === "object"
          ? (entry as Record<string, unknown>)[userKey]
          : entry;
      return ctx.userMap[id as number] ?? `#${id}`;
    });
  };

const LIST_FIELD_TOKENIZERS: Record<string, (val: unknown, ctx: LogContext) => string[]> =
  {
    assignees: tokenizeAssignees,
    materials: tokenizeMaterials,
    coordinators: tokenizeUserReferences("coordinator"),
    managers: tokenizeUserReferences("manager"),
  };

// Diffs two token lists as multisets so that unchanged entries (including duplicates)
// don't get repeated in full on both sides of the diff - only the actual additions/removals show.
function diffTokens(
  before: string[],
  after: string[]
): { added: string[]; removed: string[] } {
  const remainingBefore = [...before];
  const added: string[] = [];

  for (const token of after) {
    const idx = remainingBefore.indexOf(token);
    if (idx === -1) {
      added.push(token);
    } else {
      remainingBefore.splice(idx, 1);
    }
  }

  return { added, removed: remainingBefore };
}

function formatUserId(val: unknown, ctx: LogContext): string {
  const id = typeof val === "string" ? Number(val) : (val as number);
  return ctx.userMap[id] ?? String(val ?? "");
}

function formatStatus(val: unknown): string {
  if (typeof val === "string") return statusLabelMap[val] ?? val;
  return String(val ?? "");
}

function formatPriority(val: unknown): string {
  if (typeof val === "string") return priorityLabelMap[val] ?? val;
  return String(val ?? "");
}

function formatDelayReason(val: unknown): string {
  if (typeof val === "string") return standbyReasonLabelMap[val] ?? val;
  return String(val ?? "");
}

function formatMeasurementUnit(val: unknown): string {
  if (typeof val === "string") return measurementUnitLabelMap[val] ?? val;
  return String(val ?? "");
}

function formatResolution(val: unknown): string {
  if (typeof val === "string") return resolutionLabelMap[val] ?? val;
  return String(val ?? "");
}

function formatScalar(val: unknown): string {
  if (val === null || val === undefined || val === "") return "";
  return String(val);
}

// Date fields come back as ISO strings, which render unreadably raw
// ("2026-07-20T11:57:51.162000+00:00") unless formatted here. Anything that does not
// parse falls back to the plain value rather than showing "Invalid Date", and a cleared
// field keeps the empty-string convention the diff renderer expects.
function formatDateValue(val: unknown, template: string): string {
  if (typeof val !== "string" && typeof val !== "number") return formatScalar(val);
  const parsed = dayjs(val);
  return parsed.isValid() ? parsed.format(template) : formatScalar(val);
}

export function formatFieldValue(key: string, val: unknown, ctx: LogContext): string {
  if (val === null || val === undefined) return "";
  switch (key) {
    case "coordinator":
    case "manager":
      return formatUserId(val, ctx);
    case "status":
      return formatStatus(val);
    case "priority":
      return formatPriority(val);
    case "delay_reason":
      return formatDelayReason(val);
    case "resolution":
      return formatResolution(val);
    case "measurement_unit":
      return formatMeasurementUnit(val);
    case "report_done":
      return val ? "Sent to Customer" : "Not Sent";
    case "service":
      return ctx.serviceMap[val as string] ?? String(val);
    case "location":
      return ctx.locationMap[val as string] ?? String(val);
    case "turbine":
      return ctx.turbineMap[val as string] ?? String(val);
    case "client":
      return ctx.clientMap[val as string] ?? String(val);
    case "project":
      return ctx.projectMap[val as string] ?? String(val);
    case "start":
    case "deadline":
    case "end":
    case "expiration_date":
      return formatDateValue(val, "DD/MM/YYYY");
    case "actual_start":
    case "actual_end":
    case "sent_to_review_at":
    case "standby_from":
    case "standby_to":
      return formatDateValue(val, "DD/MM/YYYY HH:mm");
    default:
      return formatScalar(val);
  }
}

// Collects the distinct ids a set of fields reference across a ticket's worklog entries
// (both old and new values), so callers can resolve just those ids instead of a full catalog.
export function collectReferencedIds(
  entries: Array<{
    old_fields: Record<string, unknown> | null | undefined;
    new_fields: Record<string, unknown> | null | undefined;
  }>,
  fields: string[]
): Record<string, string[]> {
  const idSets: Record<string, Set<string>> = {};
  for (const field of fields) idSets[field] = new Set();

  for (const entry of entries) {
    for (const field of fields) {
      const oldVal = entry.old_fields?.[field];
      const newVal = entry.new_fields?.[field];
      if (typeof oldVal === "string") idSets[field].add(oldVal);
      if (typeof newVal === "string") idSets[field].add(newVal);
    }
  }

  return Object.fromEntries(
    Object.entries(idSets).map(([field, set]) => [field, Array.from(set)])
  );
}

export function formatActor(
  changedBy: number | null | undefined,
  userMap: Record<number, string>
): string | null {
  if (changedBy == null) return null;
  return userMap[changedBy] ?? `#${changedBy}`;
}

function truncateContent(content: string, maxLength = 80): string {
  const trimmed = content.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength).trimEnd()}…`;
}

function buildCommentDiff(entry: WorkLogDiffEntry, ctx: LogContext): FieldDiff[] {
  const oldContent = entry.old_fields?.content;
  const newContent = entry.new_fields?.content;
  const hasOldContent = typeof oldContent === "string" && oldContent !== "";
  const hasNewContent = typeof newContent === "string" && newContent !== "";

  const action = hasNewContent ? (hasOldContent ? "edited" : "added") : "deleted";

  const author = formatActor(entry.changed_by, ctx.userMap);

  const taskLabel = entry.ticket_item ? ctx.taskMap[entry.ticket_item] : null;

  const label = [
    "Comment",
    taskLabel ? `on ${taskLabel}` : null,
    action,
    author ? `by ${author}` : null,
  ]
    .filter(Boolean)
    .join(" ");

  const preview = truncateContent(
    String(hasNewContent ? newContent : (oldContent ?? ""))
  );

  return [{ label, before: "", after: "", summary: preview }];
}

export function buildFieldDiffs(entry: WorkLogDiffEntry, ctx: LogContext): FieldDiff[] {
  if (entry.ticket_comment) {
    return buildCommentDiff(entry, ctx);
  }

  const oldFields = entry.old_fields;
  const newFields = entry.new_fields;

  const allKeys = Array.from(
    new Set([...Object.keys(oldFields ?? {}), ...Object.keys(newFields ?? {})])
  );

  const diffs: FieldDiff[] = [];
  for (const key of allKeys) {
    if (SKIP_FIELDS.has(key)) continue;

    if (key === "items") {
      diffs.push(...buildItemDiffs(oldFields?.[key], newFields?.[key], ctx));
      continue;
    }

    const label = FIELD_LABELS[key] ?? key;
    const tokenize = LIST_FIELD_TOKENIZERS[key];

    if (tokenize) {
      const { added, removed } = diffTokens(
        tokenize(oldFields?.[key], ctx),
        tokenize(newFields?.[key], ctx)
      );
      if (removed.length > 0)
        diffs.push({ label, before: removed.join(", "), after: "" });
      if (added.length > 0) diffs.push({ label, before: "", after: added.join(", ") });
      continue;
    }

    const before = formatFieldValue(key, oldFields?.[key], ctx);
    const after = formatFieldValue(key, newFields?.[key], ctx);

    if (before === after) continue;

    diffs.push({ label, before, after });
  }
  return diffs;
}

export function buildLogContext(
  ticket: TicketRetrieve,
  materialMap: Record<string, string>,
  serviceCatalog: Record<string, { name: string }>,
  locationMap: Record<string, string>,
  turbineMap: Record<string, string>,
  clientMap: Record<string, string>,
  projectMap: Record<string, string>
): LogContext {
  const userMap: Record<number, string> = {};

  ticket.assignees.forEach((a) => {
    userMap[a.assignee.id] = getUserDisplayName(a.assignee);
  });
  ticket.coordinators.forEach((c) => {
    userMap[c.coordinator.id] = getUserDisplayName(c.coordinator);
  });
  ticket.managers.forEach((m) => {
    userMap[m.manager.id] = getUserDisplayName(m.manager);
  });
  userMap[ticket.created_by.id] = getUserDisplayName(ticket.created_by);

  const componentMap: Record<string, string> = {};
  ticket.items.forEach((item) => {
    if (item.component?.id) {
      const details = formatComponentDetails(item.component);
      componentMap[item.component.id] = details
        ? `${formatComponentLabel(item.component)} · ${details}`
        : formatComponentLabel(item.component);
    }
  });

  const serviceMap: Record<string, string> = {};
  Object.entries(serviceCatalog).forEach(([id, service]) => {
    serviceMap[id] = service.name;
  });

  const taskMap: Record<string, string> = {};
  ticket.items.forEach((item) => {
    if (item.id) {
      taskMap[item.id] = `Task #${item.ticket_item_number ?? "?"}`;
    }
  });

  return {
    userMap,
    materialMap,
    componentMap,
    serviceMap,
    locationMap,
    turbineMap,
    clientMap,
    projectMap,
    taskMap,
  };
}
